import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/lib/content";
import { useLang } from "@/lib/lang";
import Reveal from "@/components/places/Reveal";
import AnimatedBlob from "@/components/places/AnimatedBlob";
import SEO from "@/components/places/SEO";
import { pickLangObj, TRANSLATABLE_FIELDS } from "@/lib/pickLang";
import { Search, X, Languages } from "lucide-react";

const LANG_LABELS = { pt: "Português", en: "English", es: "Español" };
const LANG_FLAGS = { pt: "🇧🇷", en: "🇺🇸", es: "🇪🇸" };

export default function Insights() {
  const { insights: RAW_INSIGHTS, insight_topics: INSIGHT_TOPICS, insights_config } = useContent();
  const { t, lang: activeLang } = useLang();
  // CMS-controlled sort order: "newest" (default) shows most recent first, "oldest" reverses.
  // Falls back gracefully if the KV is unset.
  const sortMode = (insights_config?.sort || "newest").toLowerCase();
  const INSIGHTS = useMemo(
    () => {
      const localized = (RAW_INSIGHTS || []).map((i) => pickLangObj(i, activeLang, TRANSLATABLE_FIELDS.insight));
      // Sort by publish year, then created_at as tiebreaker (falls back to 0 when absent).
      const keyOf = (i) => `${String(i.year || "0000").padStart(4, "0")}_${i.created_at || i.updated_at || ""}`;
      const sorted = [...localized].sort((a, b) => {
        const ka = keyOf(a); const kb = keyOf(b);
        return sortMode === "oldest" ? ka.localeCompare(kb) : kb.localeCompare(ka);
      });
      return sorted;
    },
    [RAW_INSIGHTS, activeLang, sortMode]
  );
  const I = t.insight || {};
  const [topic, setTopic] = useState("");
  const [author, setAuthor] = useState("");
  const [year, setYear] = useState("");
  const [lang, setLang] = useState("all");
  const [kw, setKw] = useState("");

  const authors = [...new Set(INSIGHTS.map((i) => i.author))];
  const years = [...new Set(INSIGHTS.map((i) => i.year))].sort((a, b) => b - a);

  // Which languages actually have content?
  const availableLangs = useMemo(() => {
    const s = new Set(INSIGHTS.map((i) => i.lang || "pt"));
    return ["pt", "en", "es"].filter((l) => s.has(l));
  }, [INSIGHTS]);

  const filtered = useMemo(() => INSIGHTS.filter((i) => {
    const iLang = i.lang || "pt";
    if (lang !== "all" && iLang !== lang) return false;
    if (topic && i.topic !== topic) return false;
    if (author && i.author !== author) return false;
    if (year && String(i.year) !== year) return false;
    if (kw && !`${i.title} ${i.excerpt} ${i.topic}`.toLowerCase().includes(kw.toLowerCase())) return false;
    return true;
  }), [topic, author, year, kw, lang, INSIGHTS]);

  const clear = () => { setTopic(""); setAuthor(""); setYear(""); setKw(""); setLang("all"); };

  return (
    <>
      <SEO
        title={t.pages?.insightsTitle || "Insights"}
        description="Insights da Places sobre marketing imobiliário: narrativa, dados, branding, IA e tendências do mercado de lançamentos."
        focusKeyword="insights imobiliários"
        keywords={["blog imobiliário", "tendências", "marketing imobiliário", "branding imobiliário"]}
        breadcrumbs={[
          { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
          { name: "Insights", url: typeof window !== "undefined" ? window.location.href : "/insights" },
        ]}
      />
      <section className="theme-light pt-36 pb-12 md:pt-44 grain relative overflow-hidden">
        <AnimatedBlob className="bg-accent-yellow top-0 right-0 w-[400px] h-[400px] opacity-30" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
          <Reveal><div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">{t.pages?.insightsEyebrow || "Insights"}</div><h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95]">{t.pages?.insightsTitle || "Se inspire com nossos insights."}</h1></Reveal>
        </div>
      </section>

      <section className="theme-light pb-24 pt-8 md:pt-0">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          {availableLangs.length > 1 && (
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="insight-lang-filter">
              <span className="text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-1.5"><Languages className="w-3.5 h-3.5" /> {I.langFilter || "Idioma"}</span>
              <button onClick={() => setLang("all")} data-testid="insight-lang-all" className={`text-xs px-3 py-1.5 rounded-full border transition ${lang === "all" ? "bg-black text-white border-black" : "border-black/15 hover:bg-black/5"}`}>{I.langAll || "Todos"}</button>
              {availableLangs.map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`insight-lang-${l}`} className={`text-xs px-3 py-1.5 rounded-full border transition flex items-center gap-1 ${lang === l ? "bg-black text-white border-black" : "border-black/15 hover:bg-black/5"}`}>
                  <span>{LANG_FLAGS[l]}</span> {LANG_LABELS[l]}
                </button>
              ))}
            </div>
          )}
          {/* Filtros — layout responsivo:
              mobile: 2 colunas (assunto+autor / ano+palavra-chave)
              desktop: 4 colunas em linha única (assunto / autor / ano / palavra-chave)
              Botão "Limpar" sai da grid e vai ao final. */}
          <div className="border border-black/10 p-4 md:p-5 mb-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <select value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="insight-topic" className="text-sm border border-black/15 px-3 py-2 rounded-md bg-white w-full"><option value="">Assunto</option>{INSIGHT_TOPICS.map((t) => <option key={t}>{t}</option>)}</select>
              <select value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="insight-author" className="text-sm border border-black/15 px-3 py-2 rounded-md bg-white w-full"><option value="">Autor</option>{authors.map((a) => <option key={a}>{a}</option>)}</select>
              <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="insight-year" className="text-sm border border-black/15 px-3 py-2 rounded-md bg-white w-full"><option value="">Ano</option>{years.map((y) => <option key={y}>{y}</option>)}</select>
              <div className="flex items-center gap-2 border border-black/15 rounded-md px-3 py-2 w-full"><Search className="w-4 h-4 text-neutral-500 shrink-0" /><input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="Palavra-chave..." className="bg-transparent outline-none text-sm flex-1 min-w-0" data-testid="insight-kw" /></div>
            </div>
            <div className="flex justify-end mt-3">
              <button onClick={clear} className="text-xs flex items-center gap-1 px-3 py-1.5 hover:bg-black/5 rounded-md border border-black/10" data-testid="insight-clear"><X className="w-3 h-3" /> Limpar</button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((i, idx) => (
              <Reveal key={i.slug} delay={idx * 0.04}>
                <Link to={`/insights/${i.slug}`} className="group block" data-testid={`insight-${i.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-200 relative">
                    <img src={i.cover} alt={i.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {i.lang && i.lang !== "pt" && <span className="absolute top-3 right-3 text-[10px] bg-white/90 backdrop-blur px-2 py-1 rounded-full font-semibold uppercase">{LANG_FLAGS[i.lang]} {i.lang}</span>}
                  </div>
                  <div className="pt-4 flex items-center gap-2"><span className="text-xs px-2 py-0.5 bg-black text-white">{i.topic}</span><span className="text-xs text-neutral-500">{i.readMin} min · {i.year}</span></div>
                  <div className="font-display text-xl mt-3 leading-snug">{i.title}</div>
                  <div className="text-xs text-neutral-500 mt-2">{i.author} · {i.role}</div>
                </Link>
              </Reveal>
            ))}
            {filtered.length === 0 && <div className="text-neutral-500 py-12 col-span-full text-center">Nenhum insight encontrado.</div>}
          </div>
        </div>
      </section>
    </>
  );
}
